/**
 * IdempotencyStore (AJIP-16.02.006 · WS10)
 * ---------------------------------------------------------------------
 * Deterministic, bounded, in-memory idempotency for activation requests.
 *
 * Founder Ruling A3:
 *   In-memory only. Persistent idempotency is not authorised.
 *
 * Contract
 *   has(requestId) → boolean
 *   get(requestId) → ActivationResult | null
 *   set(requestId, activationResult, { compositeKey? }) → void
 *   beginInFlight(requestId) → true (throws DuplicateActivationError on collision)
 *   endInFlight(requestId) → boolean
 *   size() → number
 *   compositeKey({ requestId, principalRef, journeyId, journeyVersion })
 */
import { DuplicateActivationError } from './ResolutionActivationErrors';
import { fnv1a } from '../persistence/Hash';

const DEFAULT_MAX_ENTRIES = 512;
const DEFAULT_TTL_MS = 15 * 60 * 1000; // 15 minutes

class IdempotencyStore {
  constructor({ maxEntries = DEFAULT_MAX_ENTRIES, ttlMs = DEFAULT_TTL_MS, nowFn = () => Date.now() } = {}) {
    this._max = maxEntries;
    this._ttl = ttlMs;
    this._now = nowFn;
    this._entries = new Map(); // requestId -> { result, expiresAt }
    this._inFlight = new Set();
  }

  compositeKey({ requestId, principalRef = null, journeyId = null, journeyVersion = null } = {}) {
    const material = `${requestId}::${principalRef == null ? 'anon' : principalRef}::${journeyId || ''}::${journeyVersion == null ? '' : journeyVersion}`;
    return `idm_${fnv1a(material)}`;
  }

  _evictIfNeeded() {
    if (this._entries.size <= this._max) return;
    const excess = this._entries.size - this._max;
    let removed = 0;
    // Map iteration is insertion-ordered → LRU-ish by age.
    for (const key of this._entries.keys()) {
      if (removed >= excess) break;
      this._entries.delete(key);
      removed += 1;
    }
  }

  _isExpired(entry) {
    if (!entry) return true;
    return this._now() > entry.expiresAt;
  }

  has(requestId) {
    if (typeof requestId !== 'string' || requestId.length === 0) return false;
    const entry = this._entries.get(requestId);
    if (!entry) return false;
    if (this._isExpired(entry)) {
      this._entries.delete(requestId);
      return false;
    }
    return true;
  }

  get(requestId) {
    if (!this.has(requestId)) return null;
    return this._entries.get(requestId).result;
  }

  set(requestId, activationResult) {
    if (typeof requestId !== 'string' || requestId.length === 0) {
      throw new Error('IdempotencyStore.set requires non-empty requestId');
    }
    if (!activationResult || typeof activationResult !== 'object') {
      throw new Error('IdempotencyStore.set requires an ActivationResult object');
    }
    this._entries.set(requestId, {
      result: activationResult,
      expiresAt: this._now() + this._ttl,
    });
    this._evictIfNeeded();
  }

  beginInFlight(requestId) {
    if (typeof requestId !== 'string' || requestId.length === 0) {
      throw new Error('IdempotencyStore.beginInFlight requires non-empty requestId');
    }
    if (this._inFlight.has(requestId)) {
      throw new DuplicateActivationError(
        `Activation for requestId '${requestId}' is already in flight`,
        { requestId },
      );
    }
    this._inFlight.add(requestId);
    return true;
  }

  endInFlight(requestId) {
    return this._inFlight.delete(requestId);
  }

  isInFlight(requestId) {
    return this._inFlight.has(requestId);
  }

  size() {
    return this._entries.size;
  }

  clear() {
    this._entries.clear();
    this._inFlight.clear();
  }
}

export { IdempotencyStore, DEFAULT_MAX_ENTRIES, DEFAULT_TTL_MS };
